import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import DateInput from '@/shared/components/DateInput';
import TrashIcon from '@/shared/components/TrashIcon';

interface Props {
  idPrefix: string;
  from: UseFormRegisterReturn;
  to: UseFormRegisterReturn;
  fromError?: FieldError;
  toError?: FieldError;
  autoFocus?: boolean;
  disabled?: boolean;
  removeLabel?: string;
  onRemove?: () => void;
  children?: ReactNode;
}

export default function DateSpanRow({
  idPrefix,
  from,
  to,
  fromError,
  toError,
  autoFocus = false,
  disabled = false,
  removeLabel,
  onRemove,
  children,
}: Props) {
  const intl = useIntl();
  const error = fromError ?? toError;

  return (
    <div className="min-w-0 px-3 py-3 sm:px-4">
      <div className="flex min-w-0 flex-wrap items-start gap-3">
        <div className="grid min-w-0 flex-1 grid-cols-2 gap-3 sm:contents">
          <label className="min-w-0 flex-1 sm:w-36 sm:flex-none">
            <span className="mb-1 block text-xs font-semibold text-slate-700">
              {intl.formatMessage({ id: 'accounts.configForm.spanFrom' })}
            </span>
            <DateInput
              {...from}
              id={`${idPrefix}-from`}
              density="dense"
              autoFocus={autoFocus}
              disabled={disabled}
              hasError={!!fromError}
            />
          </label>

          <label className="min-w-0 flex-1 sm:w-36 sm:flex-none">
            <span className="mb-1 block text-xs font-semibold text-slate-700">
              {intl.formatMessage({ id: 'accounts.configForm.spanTo' })}
            </span>
            <DateInput
              {...to}
              id={`${idPrefix}-to`}
              density="dense"
              disabled={disabled}
              hasError={!!toError}
            />
          </label>
        </div>

        {onRemove && removeLabel && (
          <div className="shrink-0 sm:order-2 sm:ml-auto">
            <LabelSpacer />
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              aria-label={removeLabel}
              className="flex h-9 items-center rounded-lg px-3 text-slate-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400 sm:px-2"
            >
              <TrashIcon />
            </button>
          </div>
        )}

        {children ? (
          <div className="w-full sm:order-1 sm:w-auto sm:min-w-[14rem] sm:flex-1">{children}</div>
        ) : null}
      </div>

      {error && <p className="mt-1.5 text-xs font-medium text-red-500">{error.message}</p>}
    </div>
  );
}

function LabelSpacer() {
  return <span aria-hidden="true" className="mb-1 block text-xs">&nbsp;</span>;
}
